import { loadStripe, type Stripe, type Appearance } from "@stripe/stripe-js";

/**
 * Appearance passed to <Elements> so the Stripe-hosted card iframe matches the
 * app's design tokens (colours, radius, typography) instead of Stripe's default
 * theme. Values mirror the light-theme token values in globals.css; Stripe's
 * iframe can't read our CSS variables, so they're resolved to concrete colours.
 */
export const stripeAppearance: Appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "hsl(0, 71%, 54%)",
    colorText: "hsl(210, 25%, 12%)",
    colorTextSecondary: "hsl(214, 14%, 34%)",
    colorTextPlaceholder: "hsl(214, 12%, 55%)",
    colorDanger: "hsl(0, 72%, 51%)",
    colorBackground: "hsl(0, 0%, 100%)",
    borderRadius: "9px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSizeBase: "15px",
  },
  rules: {
    ".Input": { borderColor: "hsl(214, 20%, 88%)" },
    ".Input:focus": {
      borderColor: "hsl(189, 90%, 38%)",
      boxShadow: "0 0 0 3px hsl(189 90% 38% / 0.14)",
    },
    ".Label": { color: "hsl(214, 14%, 34%)", fontWeight: "600" },
  },
};

/**
 * Stripe.js publishable-key loader, memoized to a single promise.
 *
 * `loadStripe` must be called exactly once per key at module scope so the
 * <Elements> provider always receives a stable promise (re-creating it remounts
 * the iframe and drops card state). The publishable key is resolved at runtime
 * from `window.__APP_CONFIG__` (injected per environment by the Caddy container,
 * see compose/production/caddy/entrypoint), falling back to the Vite build-time
 * env var (`VITE_STRIPE_PUBLIC_KEY`) for local dev. When it's absent we resolve
 * to `null` so the payment UI can render a clear "not configured" message
 * instead of throwing.
 */
const publishableKey =
  window.__APP_CONFIG__?.STRIPE_PUBLIC_KEY ||
  (import.meta.env.VITE_STRIPE_PUBLIC_KEY as string | undefined);

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!publishableKey) return Promise.resolve(null);
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

export const isStripeConfigured = Boolean(publishableKey);
